import type { components } from "@/api/schema";

export type InboundDoc = components["schemas"]["InboundDocument"];

export type InboundSortKey =
  | "doc_no"
  | "doc_type"
  | "load_date"
  | "load_time"
  | "sku"
  | "quantity"
  | "tran_type"
  | "ref_no";
export type SortDir = "asc" | "desc";

export interface InboundFilterState {
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string;
  search: string;
  sortKey: InboundSortKey;
  sortDir: SortDir;
  offset: number;
  limit: number;
}

export const DEFAULT_LIMIT = 50;

/** YYYY-MM-DD for `d`. */
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Default filter: last 7 days, newest first. */
export function defaultInboundFilter(now: Date = new Date()): InboundFilterState {
  const from = new Date(now);
  from.setDate(from.getDate() - 6);
  return {
    dateFrom: isoDate(from),
    dateTo: isoDate(now),
    search: "",
    sortKey: "load_date",
    sortDir: "desc",
    offset: 0,
    limit: DEFAULT_LIMIT,
  };
}

/** Maps filter state to the API query object (snake_case, omit empties). */
export function filterToQuery(f: InboundFilterState): {
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_key?: InboundSortKey;
  sort_dir?: SortDir;
  offset?: number;
  limit?: number;
} {
  const q: {
    date_from?: string;
    date_to?: string;
    search?: string;
    sort_key?: InboundSortKey;
    sort_dir?: SortDir;
    offset?: number;
    limit?: number;
  } = {
    sort_key: f.sortKey,
    sort_dir: f.sortDir,
    offset: f.offset,
    limit: f.limit,
  };
  if (f.dateFrom) q.date_from = f.dateFrom;
  if (f.dateTo) q.date_to = f.dateTo;
  if (f.search) q.search = f.search;
  return q;
}

/** Toggles sort for a column: same column flips dir, new column starts desc. */
export function toggleSort(f: InboundFilterState, key: InboundSortKey): InboundFilterState {
  if (f.sortKey === key) {
    return { ...f, sortDir: f.sortDir === "asc" ? "desc" : "asc", offset: 0 };
  }
  return { ...f, sortKey: key, sortDir: "desc", offset: 0 };
}
